import { ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import c from 'classnames';

import { Icon } from '@repo/ui';
import { useApplyScrollIntent } from 'hooks/useApplyScrollIntent';

import { ActivationBackButton } from '../ActivationHeader/ActivationBackButton';

import styles from './ActivationScreen.module.css';

export type ActivationScreenProps = {
  /** Pinned to the top: stepper, progress. */
  header: ReactNode;
  onBack?: () => void;
  onClose?: () => void;
  /**
   * For stepper headers: centers `header` in the content column and, on
   * mobile, puts it on the same row as the back and close buttons.
   */
  inlineHeader?: boolean;
  /** The only region that scrolls. */
  children: ReactNode;
  /** Pinned to the bottom: primary action and secondary links. */
  footer?: ReactNode;
  /** For content that scrolls by itself (an iframe): fills the body, which then never scrolls. */
  stretchBody?: boolean;
  /** Host for ActivationFooter portals, mounted in the footer. Hides while empty. */
  footerNode?: HTMLElement;
  /** The body scrolls back to the top whenever this value changes. */
  scrollResetKey?: unknown;
  className?: string;
};

/**
 * Viewport-height screen for the redesigned activation flow: header on top,
 * footer at the bottom, and only the body scrolls.
 */
export const ActivationScreen = ({
  header,
  onBack,
  onClose,
  inlineHeader,
  children,
  footer,
  stretchBody,
  footerNode,
  scrollResetKey,
  className,
}: ActivationScreenProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const footerContentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const host = footerContentRef.current;
    if (!host || !footerNode) return;
    host.appendChild(footerNode);
    return () => footerNode.remove();
  }, [footerNode]);

  useApplyScrollIntent(bodyRef);

  const lastResetKeyRef = useRef(scrollResetKey);

  useEffect(() => {
    if (Object.is(lastResetKeyRef.current, scrollResetKey)) return;
    lastResetKeyRef.current = scrollResetKey;
    bodyRef.current?.scrollTo?.(0, 0);
  }, [scrollResetKey]);

  return (
    <div
      className={c(styles.screen, stretchBody && styles.stretch, className)}
    >
      <div className={styles.header}>
        <div
          className={c(
            styles.headerRow,
            (onBack || onClose) && styles.withControls,
            inlineHeader && styles.inline,
          )}
          data-testid='activation-screen-header-row'
        >
          {onBack && (
            <div className={styles.backSlot}>
              <ActivationBackButton onBack={onBack} />
            </div>
          )}
          <div className={styles.headerContent}>{header}</div>
          {onClose && (
            <div className={styles.closeSlot}>
              <button
                type='button'
                className={styles.closeButton}
                onClick={onClose}
                aria-label='Close'
                data-testid='close-button'
              >
                <Icon name='closeSm' />
              </button>
            </div>
          )}
        </div>
      </div>
      <div
        className={styles.body}
        ref={bodyRef}
        data-testid='activation-screen-body'
      >
        {children}
      </div>
      {(footer || footerNode) && (
        <div className={styles.footer} data-testid='activation-screen-footer'>
          <div className={styles.footerContent} ref={footerContentRef}>
            {footer}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationScreen;
